import { when } from '@execonline-inc/maybe-adapter';
import { assertNever } from '@kofno/piper';
import { action, computed, makeObservable, observable } from 'mobx';
import { error } from '../ErrorHandling';
import {
  getSearchParametersFromUrl,
  getUpdatedSearchParameters,
  getUpdatedSortParameters,
} from '../ListFilter';
import { SearchParameter, SearchParameters } from '../ListFilter/Types';
import { Link } from '../Resource/Types';
import { TPlainTextKey } from '../Translations';
import GroupsOptionsStore from './Options';
import { State as GroupsOptionsState } from './Options/Types';
import {
  GroupResource,
  GroupResources,
  GroupsResource,
  initializing,
  loading,
  loadingMore,
  Ready,
  ready,
  SortableColumn,
  sorting,
  State,
  updatingOrganizationParameters,
  updatingQuery,
  updatingSearchParameters,
} from './Types';
import { StringOption } from '../components/Options/Types';

class GroupsStore {
  @observable
  state: State = initializing(getSearchParametersFromUrl());

  readonly optionsStore: GroupsOptionsStore;

  constructor() {
    makeObservable(this);
    this.optionsStore = new GroupsOptionsStore(this);
  }

  updatedSearchParameters = (searchParameter: SearchParameter, state: Ready) =>
    getUpdatedSearchParameters(
      {
        newSearchParameter: searchParameter,
        searchParameters: state.searchParameters,
      },
      'user_groups_filter',
    );

  @action
  loading = (page: number) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'updating-search-parameters':
      case 'sorting':
      case 'ready':
        this.state = loading(this.state, page);
        break;
      case 'loading-more':
      case 'updating-query':
      case 'loading':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadingMore = (page: number) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = loadingMore(this.state, page);
        break;
      case 'initializing':
      case 'updating-search-parameters':
      case 'sorting':
      case 'updating-query':
      case 'loading':
      case 'loading-more':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  ready = (groupsResource: GroupsResource) => {
    switch (this.state.kind) {
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
        this.state = ready(this.state, groupsResource);
        break;
      case 'sorting':
      case 'initializing':
      case 'ready':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updatingQuery = (searchParameter: SearchParameter) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
        break;
      case 'ready':
        const searchParameters = this.updatedSearchParameters(searchParameter, this.state);
        this.state = updatingQuery(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updatingSearchParameters = (searchParameter: SearchParameter) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
        break;
      case 'ready':
        const searchParameters = this.updatedSearchParameters(searchParameter, this.state);
        this.state = updatingSearchParameters(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updatingOrganizationParameters = (option: StringOption) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
        break;
      case 'ready':
        this.state = updatingOrganizationParameters(this.state, option);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  sorting = (sortedColumn: SortableColumn) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
        break;
      case 'ready':
        const searchParameters = getUpdatedSortParameters(
          { searchParameters: this.state.searchParameters, sortedColumn },
          'user_groups_filter',
        );
        this.state = sorting(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  error = (msg: TPlainTextKey) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'ready':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
        this.state = error(msg);
        break;
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  pageTo = (page: number) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = loading(this.state, page);
        break;
      case 'loading-more':
      case 'loading':
      case 'initializing':
      case 'sorting':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  concatNewUserGroup = (state: Ready, resource: GroupResources): GroupsResource => ({
    ...state.groupsResource,
    payload: resource.concat(state.groupsResource.payload),
  });

  replaceGroupInGroups = (state: Ready, resource: GroupResource) => {
    return {
      ...state.groupsResource,
      payload: state.groupsResource.payload.map((r) =>
        when(r.payload.id === resource.payload.id, resource).getOrElseValue(r),
      ),
    };
  };

  @action
  addGroupToGroups = (resource: GroupResource) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = ready(this.state, this.concatNewUserGroup(this.state, [resource]));
        break;
      case 'loading-more':
      case 'loading':
      case 'initializing':
      case 'sorting':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateGroupInGroups = (resource: GroupResource) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = ready(this.state, this.replaceGroupInGroups(this.state, resource));
        break;
      case 'loading-more':
      case 'loading':
      case 'initializing':
      case 'sorting':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get searchParameters(): SearchParameters {
    switch (this.state.kind) {
      case 'ready':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'loading':
      case 'loading-more':
      case 'initializing':
        return this.state.searchParameters;
      case 'error':
        return [];
    }
  }

  @computed
  get links(): ReadonlyArray<Link> {
    switch (this.state.kind) {
      case 'ready':
        return this.state.groupsResource.links;
      case 'loading':
      case 'loading-more':
      case 'initializing':
      case 'error':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
        return [];
    }
  }

  @computed
  get optionsState(): GroupsOptionsState {
    switch (this.state.kind) {
      case 'ready':
        return { kind: 'loading', links: this.links };
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'sorting':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'error':
        return this.optionsStore._state;
    }
  }
}

const groupsStore = new GroupsStore();

export { groupsStore, GroupsStore };
export default GroupsStore;
